/**
 * Générateur — Automatisme : reconnaître un carré, un rectangle, un triangle
 * Attendu ID : 1016 | Niveau : 6ème | Domaine : GM
 * Rubrique : AUTOMATISME (programme 2025) — « Il reconnaît un carré, un
 * rectangle, un triangle. »
 * Labels (cartes exerciseur) :
 *   - Reconnaître un carré, un rectangle, un triangle
 *
 * ── Plan de cartes (2026-08-22, validé prof) ──────────────────────────────
 * Trois formats :
 *   F1 reconnaitre_qcm — planche de 4 figures numérotées ①..④ en POSITIONS
 *      VARIÉES (rotation quelconque, demande explicite du programme : un
 *      carré posé sur la pointe reste un carré), cible ∈ {carré, rectangle,
 *      triangle}, réponse unique par construction sous définition INCLUSIVE :
 *      cible rectangle → JAMAIS de carré dans la planche ; cible carré → le
 *      LOSANGE toujours présent (le distracteur qui teste l'angle droit et
 *      non l'allure — vu en CM1/CM2, légitime) ; cible triangle → un seul
 *      triangle dans la planche. La planche est CODÉE (arbitrage prof
 *      2026-08-22, régime « lecture rigoureuse » : sans codage, la seule
 *      réponse rigoureuse serait « on ne peut pas savoir » — et le codage
 *      rend le losange/carré DÉMONTRABLE) ; la géométrie sous le codage
 *      reste exacte par construction, les non-cibles éloignées d'au
 *      moins ~15 %.
 *   F2 forcement_qcm — « Un quadrilatère a 4 côtés de N cm. Est-ce
 *      forcément un carré ? » → « Non : ses angles ne sont pas forcément
 *      droits » (reformat prof 2026-08-22 : l'incertitude est DANS la
 *      question, la justification DANS les choix — la v1 « quelle est
 *      cette figure à coup sûr / aucune des trois » était ambiguë sur la
 *      complétude de la description). Six gabarits dont deux d'INCLUSION
 *      (« 4 angles droits → forcément un rectangle » : Oui).
 *   F3 inclusion_vf — jumelles V/F sur les inclusions (un carré EST un
 *      rectangle / un rectangle n'est pas toujours un carré...) + lignes
 *      définitoires ; paires miroir conservées DANS le même tirage
 *      (arbitrage prof récurrent : détecte les réponses au hasard).
 * tirageSansRemise sur F2 et F3 (petits domaines).
 */
import {
    randomChoice, randInt, randomChoiceMultiple, shuffle,
    makeQcm, makeVraiFaux, tirageSansRemise,
} from '../../../utils';

const ATTENDU_ID = 1016;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L1 = 'Reconnaître un carré, un rectangle, un triangle';

const dataUrl = (svg) => `data:image/svg+xml,${encodeURIComponent(svg)}`;
const elImage = (url) => ({ version: 1, elements: [{ type: 'image', config: { imageUrl: url } }] });

const BLEU = '#1E3A8A';
const NUMS = ['①', '②', '③', '④'];

// ─── Géométrie (coordonnées locales EXACTES) ──────────────────────────────

const rot = (angleDeg) => {
    const a = (angleDeg * Math.PI) / 180;
    return ([x, y]) => [x * Math.cos(a) - y * Math.sin(a), x * Math.sin(a) + y * Math.cos(a)];
};
const dist = (P, Q) => Math.hypot(P[0] - Q[0], P[1] - Q[1]);
const cosSommet = (pts, i) => {
    const n = pts.length;
    const S = pts[i];
    const U = pts[(i + n - 1) % n];
    const V = pts[(i + 1) % n];
    return ((U[0] - S[0]) * (V[0] - S[0]) + (U[1] - S[1]) * (V[1] - S[1])) / (dist(S, U) * dist(S, V));
};

/**
 * Constructeurs. Chaque entrée rend { pts } dont la CLASSIFICATION est
 * garantie par construction : le carré exact, le rectangle de ratio ≥ 1,4,
 * le losange sans angle à moins de ~16° du droit, les triangles sans
 * ambiguïté, le trapèze rectangle avec exactement 2 angles droits, le
 * pentagone quelconque.
 */
const FIGURES = {
    carre: () => {
        const c = randInt(50, 66);
        return { pts: [[0, -c], [0, 0], [c, 0], [c, -c]] };
    },
    rectangle: () => {
        const h = randInt(42, 56);
        const w = Math.round(h * (1.4 + Math.random() * 0.4));
        return { pts: [[0, -h], [0, 0], [w, 0], [w, -h]] };
    },
    losange: () => {
        const p = randInt(46, 58);
        const q = Math.round(p * (0.55 + Math.random() * 0.2));
        return { pts: [[-p, 0], [0, -q], [p, 0], [0, q]] };
    },
    triangle_rect: () => {
        const a = randInt(46, 62);
        const b = Math.round(a * (1.3 + Math.random() * 0.4));
        return { pts: [[0, -a], [0, 0], [b, 0]] };
    },
    triangle_iso: () => {
        for (let essai = 0; essai < 30; essai++) {
            const base = randInt(52, 70);
            const h = Math.round(base * (0.8 + Math.random() * 0.45));
            const pts = [[base / 2, -h], [0, 0], [base, 0]];
            if (Math.abs(base - dist(pts[0], pts[1])) < 0.2 * dist(pts[0], pts[1])) continue;
            return { pts };
        }
        return { pts: [[32, -62], [0, 0], [64, 0]] };
    },
    triangle_qcq: () => {
        for (let essai = 0; essai < 40; essai++) {
            const w = randInt(55, 75);
            const x3 = randInt(-15, w + 15);
            const h = randInt(35, 60);
            const pts = [[0, 0], [w, 0], [x3, -h]];
            const cotes = [dist(pts[0], pts[1]), dist(pts[1], pts[2]), dist(pts[2], pts[0])];
            // ni isocèle (côtés ≠ 12 %), ni rectangle (angles loin du droit),
            // ni aplati ni effilé
            const paireProche = cotes.some((a, i) => cotes.some((b, j) =>
                i < j && Math.abs(a - b) < 0.12 * Math.max(a, b)));
            if (paireProche) continue;
            const coss = pts.map((_, i) => cosSommet(pts, i));
            if (coss.some((c) => Math.abs(c) < 0.12 || c > 0.9 || c < -0.85)) continue;
            return { pts };
        }
        return { pts: [[0, 0], [72, 0], [30, -30]] };
    },
    trapeze_rect: () => {
        for (let essai = 0; essai < 30; essai++) {
            const h = randInt(42, 56);
            const w1 = Math.round(h * (1.4 + Math.random() * 0.4));
            const w2 = Math.round(w1 * (0.5 + Math.random() * 0.2));
            if (Math.abs(w2 - h) < 0.15 * h) continue;
            return { pts: [[0, 0], [0, -h], [w2, -h], [w1, 0]] };
        }
        return { pts: [[0, 0], [0, -52], [40, -52], [82, 0]] };
    },
    pentagone: () => {
        for (let essai = 0; essai < 40; essai++) {
            const w = randInt(52, 68);
            const d = randInt(18, 28);
            const h1 = randInt(30, 44);
            const h2 = h1 + randInt(22, 34);
            const j = randomChoice([-1, 1]) * randInt(8, 16);
            const pts = [[0, 0], [w, 0], [w + d, -h1], [w / 2 + j, -h2], [-d, -h1]];
            const cd = dist(pts[2], pts[3]);
            const de = dist(pts[3], pts[4]);
            if (Math.abs(cd - de) < 0.15 * Math.max(cd, de)) continue;
            if (Math.abs(cosSommet(pts, 0)) < 0.12 || Math.abs(cosSommet(pts, 1)) < 0.12) continue;
            return { pts };
        }
        return { pts: [[0, 0], [52, 0], [73, -37], [19, -67], [-21, -37]] };
    },
};

const TRIANGLES = ['triangle_rect', 'triangle_iso', 'triangle_qcq'];

// ─── La planche 2×2 ───────────────────────────────────────────────────────

/** Pose une figure : rotation quelconque puis recadrage dans sa case. */
const poserDansCase = (pts, cx, cy) => {
    const r = rot(randInt(0, 359));
    let t = pts.map(r);
    const minX = Math.min(...t.map((p) => p[0]));
    const minY = Math.min(...t.map((p) => p[1]));
    t = t.map(([x, y]) => [x - minX, y - minY]);
    const W = Math.max(...t.map((p) => p[0]));
    const H = Math.max(...t.map((p) => p[1]));
    const s = Math.min(1, 124 / W, 86 / H);
    const ox = cx + 18 + (124 - W * s) / 2;
    const oy = cy + 22 + (86 - H * s) / 2;
    return t.map(([x, y]) => [ox + x * s, oy + y * s]);
};

const CASES = [[0, 0], [156, 0], [0, 116], [156, 116]];

// ─── Le codage dessiné (régime « lecture rigoureuse », arbitrage prof
// 2026-08-22 : la planche est CODÉE, cohérence avec l'attendu 1015 — ce qui
// n'est pas codé ne doit pas être supposé) ────────────────────────────────

const ROUGE = '#dc2626';
const unit = (P, Q) => {
    const dd = dist(P, Q);
    return [(Q[0] - P[0]) / dd, (Q[1] - P[1]) / dd];
};

const svgCarreCode = (pts, i, k) => {
    const n = pts.length;
    const S = pts[i];
    const u = unit(S, pts[(i + n - 1) % n]);
    const v = unit(S, pts[(i + 1) % n]);
    const s = 9;
    const p1 = [S[0] + s * u[0], S[1] + s * u[1]];
    const p2 = [S[0] + s * (u[0] + v[0]), S[1] + s * (u[1] + v[1])];
    const p3 = [S[0] + s * v[0], S[1] + s * v[1]];
    return `<path d="M ${p1[0].toFixed(1)} ${p1[1].toFixed(1)} L ${p2[0].toFixed(1)} ${p2[1].toFixed(1)} L ${p3[0].toFixed(1)} ${p3[1].toFixed(1)}" fill="none" stroke="${ROUGE}" stroke-width="1.6" data-code="carre" data-fig="${k}" data-i="${i}"/>`;
};

const svgTicksCode = (pts, i, nTraits, k) => {
    const n = pts.length;
    const P = pts[i];
    const Q = pts[(i + 1) % n];
    const u = unit(P, Q);
    const nx = -u[1];
    const ny = u[0];
    const mx = (P[0] + Q[0]) / 2;
    const my = (P[1] + Q[1]) / 2;
    const out = [];
    for (let t = 0; t < nTraits; t++) {
        const off = (t - (nTraits - 1) / 2) * 4;
        const cx = mx + off * u[0];
        const cy = my + off * u[1];
        out.push(`<line x1="${(cx - 5 * nx).toFixed(1)}" y1="${(cy - 5 * ny).toFixed(1)}" x2="${(cx + 5 * nx).toFixed(1)}" y2="${(cy + 5 * ny).toFixed(1)}" stroke="${ROUGE}" stroke-width="1.8" data-code="tick" data-fig="${k}" data-cote="${i}" data-n="${nTraits}"/>`);
    }
    return out.join('\n  ');
};

/** Le codage propre à chaque nature (indices AVANT rotation — la rotation
 *  préserve les indices) : le carré = 4 traits + UN angle droit (suffisant :
 *  un losange avec un angle droit est un carré) ; le rectangle = deux paires
 *  de traits + un angle droit ; le losange = 4 traits SANS angle droit ; le
 *  trapèze rectangle = ses 2 angles droits ; triangles et pentagone nus. */
const codesDe = (forme, pts, k) => {
    const out = [];
    if (forme === 'carre' || forme === 'losange') {
        for (let i = 0; i < 4; i++) out.push(svgTicksCode(pts, i, 1, k));
        if (forme === 'carre') out.push(svgCarreCode(pts, randInt(0, 3), k));
    } else if (forme === 'rectangle') {
        // pts : [gauche(h), bas(w), droite(h), haut(w)] → paires opposées
        out.push(svgTicksCode(pts, 0, 1, k), svgTicksCode(pts, 2, 1, k));
        out.push(svgTicksCode(pts, 1, 2, k), svgTicksCode(pts, 3, 2, k));
        out.push(svgCarreCode(pts, randInt(0, 3), k));
    } else if (forme === 'trapeze_rect') {
        out.push(svgCarreCode(pts, 0, k), svgCarreCode(pts, 1, k));
    }
    return out;
};

/** La planche : 4 figures CODÉES en positions variées, numérotées ①..④.
 *  data-fig = l'index (0..3) — aucun attribut ne dit la nature : le solveur
 *  test-side classe par le CODAGE relu et contre-vérifie la géométrie. */
const svgPlanche = (formes) => {
    const p = [];
    p.push('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 312 232" width="300" role="img"');
    p.push('  aria-label="Quatre figures géométriques codées (angles droits, longueurs égales), numérotées de 1 à 4, dans des positions variées.">');
    formes.forEach((forme, k) => {
        const [cx, cy] = CASES[k];
        const pts = poserDansCase(FIGURES[forme]().pts, cx, cy);
        p.push(`  <text x="${cx + 14}" y="${cy + 20}" font-size="15" font-weight="700" fill="#111827" font-family="system-ui,sans-serif" data-num="${k}">${NUMS[k]}</text>`);
        p.push(`  <polygon points="${pts.map((pt) => `${pt[0].toFixed(1)},${pt[1].toFixed(1)}`).join(' ')}" fill="#eff6ff" stroke="${BLEU}" stroke-width="2" data-fig="${k}"/>`);
        const codes = codesDe(forme, pts, k);
        if (codes.length) p.push('  ' + codes.join('\n  '));
    });
    p.push('</svg>');
    return p.join('\n');
};

// ─── Assemblage QCM ───────────────────────────────────────────────────────

const finirQcm = (bonne, dis, texte, sousType, elements) => {
    const payload = makeQcm(
        [bonne, ...dis.map((c) => c.valeur)],
        bonne,
        texte,
        { ...opts(L1), ...(elements ? { elements_additionnels: elements } : {}) },
        false,
    );
    payload.contenu = { ...payload.contenu, sousType };
    payload._meta.params = { sousType, distracteurs: dis };
    return payload;
};

// ─── F1 : la planche « laquelle est un … ? » ──────────────────────────────

const CIBLES = {
    carre: { question: 'un carré' },
    rectangle: { question: 'un rectangle' },
    triangle: { question: 'un triangle' },
};

const genReconnaitreQcm = () => {
    const cible = randomChoice(Object.keys(CIBLES));
    let formes;
    if (cible === 'carre') {
        // le losange TOUJOURS là ; jamais deux triangles
        const pool = shuffle(['rectangle', 'trapeze_rect', 'pentagone', randomChoice(TRIANGLES)]).slice(0, 2);
        formes = ['carre', 'losange', ...pool];
    } else if (cible === 'rectangle') {
        // JAMAIS de carré (il serait une 2e bonne réponse)
        formes = ['rectangle', ...shuffle(['losange', 'trapeze_rect', 'pentagone', randomChoice(TRIANGLES)]).slice(0, 3)];
    } else {
        // un SEUL triangle dans la planche
        formes = [randomChoice(TRIANGLES), ...shuffle(['carre', 'rectangle', 'losange', 'trapeze_rect', 'pentagone']).slice(0, 3)];
    }
    formes = shuffle(formes);
    const estCible = (f) => (cible === 'triangle' ? TRIANGLES.includes(f) : f === cible);
    const idx = formes.findIndex(estCible);
    const svg = svgPlanche(formes);
    const bonne = `Figure ${NUMS[idx]}`;
    const dis = formes
        .map((f, k) => ({ f, k }))
        .filter(({ k }) => k !== idx)
        .map(({ f, k }) => ({ valeur: `Figure ${NUMS[k]}`, code: `figure_${f}` }));
    return finirQcm(bonne, dis,
        `<p><b>Parmi ces quatre figures, laquelle est ${CIBLES[cible].question} ?</b></p><p>{{element:0}}</p>`,
        `reconnaitre_${cible}`, elImage(dataUrl(svg)));
};

// ─── F2 : « Est-ce forcément un … ? » (reformat prof 2026-08-22) ──────────
// L'incertitude est DANS la question (« forcément »), plus besoin de dire si
// la description est complète. Trois choix fixes, la justification incluse :
// les raisons des « Non » distracteurs sont FACTUELLEMENT FAUSSES sur la
// description — sauf le piège d'inclusion voulu (« 4 angles droits, est-ce
// forcément un rectangle ? » : l'élève qui croit qu'un possible carré
// disqualifie le rectangle répond Non).

const OUI = 'Oui';
const NON_ANGLES = 'Non : ses angles ne sont pas forcément droits';
const NON_COTES = 'Non : ses côtés n\'ont pas forcément tous la même longueur';
const CHOIX_FORCEMENT = [OUI, NON_ANGLES, NON_COTES];

const PHRASES_COTES = [
    (n) => `Un quadrilatère a 4 côtés de ${n} cm.`,
    (n) => `Les 4 côtés d'un quadrilatère mesurent ${n} cm.`,
];
const PHRASES_DROITS = [
    () => 'Un quadrilatère a 4 angles droits.',
    () => 'Les 4 angles d\'un quadrilatère sont droits.',
];
const PHRASES_COMPLET = [
    (n) => `Un quadrilatère a 4 côtés de ${n} cm et 4 angles droits.`,
    (n) => `Un quadrilatère a 4 angles droits et 4 côtés de ${n} cm.`,
];

const GABARITS_FORCEMENT = [
    { code: 'cotes_carre', phrases: PHRASES_COTES, cible: 'un carré', bonne: NON_ANGLES },
    { code: 'droits_carre', phrases: PHRASES_DROITS, cible: 'un carré', bonne: NON_COTES },
    { code: 'complet_carre', phrases: PHRASES_COMPLET, cible: 'un carré', bonne: OUI },
    // les deux items d'INCLUSION : 4 angles droits (voire un carré complet)
    // → c'est forcément un rectangle, même s'il est peut-être aussi un carré
    { code: 'droits_rectangle', phrases: PHRASES_DROITS, cible: 'un rectangle', bonne: OUI },
    { code: 'complet_rectangle', phrases: PHRASES_COMPLET, cible: 'un rectangle', bonne: OUI },
    { code: 'cotes_rectangle', phrases: PHRASES_COTES, cible: 'un rectangle', bonne: NON_ANGLES },
];

// Le VERDICT est tiré uniformément d'abord (mesuré : 3 gabarits sur 6
// répondent Oui → « toujours Oui » gagnait 50 %), puis un gabarit compatible,
// puis un sac sans remise PAR gabarit pour la variété interne.
const genForcementQcm = () => {
    const verdict = randomChoice(CHOIX_FORCEMENT);
    const g = randomChoice(GABARITS_FORCEMENT.filter((x) => x.bonne === verdict));
    return tirageSansRemise(
        `gen_1016:forcement:${g.code}`,
        () => {
            const phrase = randomChoice(g.phrases)(randInt(3, 9));
            const dis = CHOIX_FORCEMENT.filter((c) => c !== g.bonne)
                .map((valeur) => ({
                    valeur,
                    code: valeur === OUI ? 'oui_a_tort'
                        : (valeur === NON_ANGLES ? 'non_angles' : 'non_cotes'),
                }));
            return finirQcm(g.bonne, dis,
                `<p>${phrase}</p><p><b>Est-ce forcément ${g.cible} ?</b></p>`,
                g.code);
        },
        (p) => String(p.texte),
    );
};

// ─── F3 : les inclusions en V/F (jumelles dans le même tirage) ────────────

const PAIRES_VF = [
    {
        cle: 'carre_rectangle',
        v: 'Un carré est aussi un rectangle.',
        f: 'Un rectangle est toujours un carré.',
    },
    {
        cle: 'def_rectangle',
        v: 'Un rectangle a toujours 4 angles droits.',
        f: 'Un rectangle a toujours 4 côtés de la même longueur.',
    },
    {
        cle: 'def_carre',
        v: 'Un carré a 4 côtés de la même longueur.',
        f: 'Un carré peut avoir un angle qui n\'est pas droit.',
    },
    {
        cle: 'def_triangle',
        v: 'Un triangle a toujours 3 côtés.',
        f: 'Un triangle peut avoir 4 côtés.',
    },
    // le losange est HORS automatisme (correction prof 2026-08-22) : aucune
    // affirmation V/F ne porte sur lui — il ne reste que distracteur muet
    // sur la planche F1.
];

const genInclusionVf = () => tirageSansRemise(
    'gen_1016:vf',
    () => {
        const paires = randomChoiceMultiple(PAIRES_VF, 2);
        const lignes = shuffle(paires.flatMap((p) => [
            { texte: p.v, verdict: 0 },
            { texte: p.f, verdict: 1 },
        ]));
        const payload = makeVraiFaux(
            lignes.map((l) => l.texte),
            lignes.map((l) => l.verdict),
            '<p><b>Vrai ou faux ?</b></p>',
            opts(L1),
        );
        payload.contenu = { ...payload.contenu, sousType: 'inclusion_vf' };
        payload._meta.params = { sousType: 'inclusion_vf', cles: paires.map((p) => p.cle) };
        return payload;
    },
    (p) => p._meta.params.cles.slice().sort().join('+'),
);

// ─── Contrat distracteurs (référence de revue) ────────────────────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'définition INCLUSIVE partout : un carré EST un rectangle et un losange — la planche et les descriptions sont construites pour que la réponse reste unique malgré l\'inclusion',
        'positions variées (rotation quelconque) : le carré sur la pointe reste un carré — demande explicite du programme',
        'la planche est CODÉE (arbitrage prof 2026-08-22, régime « lecture rigoureuse », cohérence avec le 1015) : 4 traits + un angle droit = carré, deux paires + un angle droit = rectangle, 4 traits SANS angle droit = losange, le trapèze ses 2 angles droits ; triangles et pentagone nus — et la géométrie sous le codage reste exacte par construction',
    ],
    reconnaitre_qcm: {
        fixes: {
            figure_losange: 'le distracteur central de la cible carré (4 côtés égaux SANS angle droit) — toujours présent quand on cherche le carré',
            figure_rectangle: 'ratio ≥ 1,4 : jamais confondable avec le carré',
            figure_trapeze_rect: '2 angles droits seulement — teste « 4 angles droits », pas « des angles droits »',
        },
        interdits: ['un carré dans la planche quand la cible est « rectangle » (2e bonne réponse)', 'deux triangles quand la cible est « triangle »'],
    },
    forcement_qcm: {
        fixes: {
            choix: 'Oui / Non-angles / Non-côtés — les raisons des Non distracteurs sont FACTUELLEMENT FAUSSES sur la description',
            piege_inclusion: '« 4 angles droits (et même un carré complet), est-ce forcément un rectangle ? » → Oui : le Non capture l\'élève pour qui carré exclut rectangle',
        },
    },
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    reconnaitre_qcm: genReconnaitreQcm,
    forcement_qcm: genForcementQcm,
    inclusion_vf: genInclusionVf,
};

const FORMAT_LABELS = {
    reconnaitre_qcm: 'Reconnaître sur une planche',
    forcement_qcm: 'Est-ce forcément… ?',
    inclusion_vf: 'Vrai ou faux : les inclusions',
};

export const CATEGORIES = [
    {
        id: 'reconnaitre',
        label: L1,
        formatsList: Object.keys(GENERATORS).map((k) => ({ key: k, label: FORMAT_LABELS[k] || k })),
    },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
